"use client";

import { useState } from "react";

type Props = {
  /** Id of the field this belongs to; the panel id is built from it. */
  id: string;
  disclosure?: { title: string; text: string };
};

/**
 * The port of fragments/forms/fields/disclosure.php: the "What is a UKPRN?"
 * style helper that sits under a field, a button that opens a short
 * explanation. The panel starts closed, and closed means hidden rather than
 * merely invisible, so nothing in it is read or tabbed to until it is opened.
 */
export default function Disclosure({ id, disclosure }: Props) {
  const [open, setOpen] = useState(false);

  if (!id || !disclosure?.title || !disclosure.text) {
    return null;
  }

  return (
    <div className="mt-1.5 js-field-disclosure">
      <button
        className="flex cursor-pointer items-center gap-[5px] border-0 bg-transparent bg-none p-0 text-[12.5px] leading-[1.5] font-medium text-brand-blue underline decoration-brand-blue/25 transition-[text-decoration-color] duration-400 ease-[ease] hover:decoration-brand-blue focus:decoration-brand-blue js-field-disclosure-toggle"
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-disclosure`}
        onClick={() => setOpen((current) => !current)}
      >
        <svg
          className="shrink-0"
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx="6.5"
            cy="6.5"
            r="6"
            stroke="currentColor"
            strokeWidth="1.2"
          />
          <path
            d="M6.5 6v3.5M6.5 4v.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>

        <span>{disclosure.title}</span>
      </button>

      <div
        className="mt-2.5 rounded-[8px] border-l-3 border-l-das-green/53 bg-brand-blue/3 px-4 py-3 js-field-disclosure-panel"
        id={`${id}-disclosure`}
        hidden={!open}
      >
        <p className="m-0 text-[13px] leading-[1.65] text-[#374151]">
          {disclosure.text}
        </p>
      </div>
    </div>
  );
}
